"use client";

import { useEffect, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

function PostHogPageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialized = useRef(false);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_SEND_TO_POSTHOG !== "true") {
      return;
    }

    // Initialize PostHog only on client side and only once
    if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_POSTHOG_KEY && !initialized.current) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: "/ingest",
        ui_host: "https://us.posthog.com",
        person_profiles: "identified_only", // Only create profiles for identified users
        capture_pageview: false, // We'll capture pageviews manually
        capture_pageleave: true,
        capture_exceptions: true,
        debug: process.env.NODE_ENV === "development",
      });
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_SEND_TO_POSTHOG !== "true") {
      return;
    }

    // Track pageviews
    if (pathname && typeof window !== "undefined" && initialized.current) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }
      posthog.capture("$pageview", {
        $current_url: url,
      });
    }
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageViewTracker />
      </Suspense>
      {children}
    </>
  );
}
